import React, { useState } from 'react';
import './DashboardMenu.less';
import maskIconOn from 'ASSETS/Images/dashboard_menu/ic_mask_stop.svg';
import maskIconOff from 'ASSETS/Images/dashboard_menu/ic_mask_start.svg';
import participantsIconOn from 'ASSETS/Images/dashboard_menu/ic_participants_stop.svg';
import participantsIconOff from 'ASSETS/Images/dashboard_menu/ic_participants_start.svg';
import recordingIconOn from 'ASSETS/Images/dashboard_menu/ic_rec_stop.svg';
import recordingIconOff from 'ASSETS/Images/dashboard_menu/ic_rec_start.svg';
import saveIcon from 'ASSETS/Images/dashboard_menu/ic_save.svg';
import { Col, Row } from 'reactstrap';
import { getMessage } from 'CONFIG/i18n';
import { socketMessage, webRtcMessageId } from 'WEBSOCKET/constants';
import { headerAction, sessionActions } from 'CONFIG/ActionFactory';
import {
  RECORDING_METHOD,
  USER_ROLES,
  Modules,
  SCREEN_MASK_MODE,
  SESSION_SUBTYPES,
} from 'UTILS/Constants';
import toastr from 'COMPONENTS/ShowTostr/ShowTostr';
import Spinner from '../Spinner/Spinner';
import { useSelector } from 'react-redux';
import { getScreenMaskSetting } from '../CommonComponents/Header/selectors';
import {
  getIsScreenMasking,
  getIsRecording,
  getRecordingMethod,
  getRecordingTarget,
  getLoggingLevel,
} from 'REDUX/Selector/SessionSelector';

const DashboardMenu = ( props ) => {
  const [ spinnerVisibility, setSpinnerVisibility ] = useState( false );

  const states = useSelector( ( state ) => ( {
    isScreenMasking: getIsScreenMasking( state ),
    screenMaskSetting: getScreenMaskSetting( state ),
    isRecording: getIsRecording( state ),
    loggingLevel: getLoggingLevel( state ),
    recordingMethod: getRecordingMethod( state ),
    recordingTarget: getRecordingTarget( state ),
  } ) );

  const isPresenter = props.uniqueId === props.presenter;

  const displayScreenMask =
		( ( ! states.screenMaskSetting && isPresenter ) ||
			( states.screenMaskSetting &&
        states.screenMaskSetting.mode === SCREEN_MASK_MODE.PRESENTER &&
				isPresenter ) ||
			( states.screenMaskSetting &&
				states.screenMaskSetting.mode === SCREEN_MASK_MODE.AGENT &&
				props.userRole === USER_ROLES.AGENT ) ) &&
		( props.modules.indexOf( Modules.SCREENSHARE ) !== -1 ||
			props.modules.indexOf( Modules.CO_BROWSE ) !== -1 );

  const displayRecording =
		props.userRole === USER_ROLES.AGENT &&
		props.modules.includes( Modules.RECORDING ) &&
		states.recordingMethod === RECORDING_METHOD.MANUAL &&
		states.recordingTarget?.length > 0 &&
    !(props.operatorExclusiveRecordingControl?.isEnable && 
      props.sessionType !== SESSION_SUBTYPES.SUPPORT_CENTER);

  const displaySave =
		props.organiser === props.uniqueId &&
		states.loggingLevel === 'ADVANCED';

  const dashBoardMenuIcons = {
    MASKING: {
      name: 'SCREEN_MASKING',
      isIcon: states.isScreenMasking,
      icon: {
        on: maskIconOn,
        off: maskIconOff,
      },
      title: {
        on: 'STOP_SCREEN_MASKING',
        off: 'START_SCREEN_MASKING',
      },
      conditions: displayScreenMask,
      onClick: () => handleMaskIcon(),
    },
    RECODING: {
      name: 'AUDIO_VIDEO_RECORDING',
      isIcon: states.isRecording,
      icon: {
        on: recordingIconOn,
        off: recordingIconOff,
      },
      title: {
        on: 'STOP_RECORDING',
        off: 'START_RECORDING',
      },
      conditions: displayRecording,
      onClick: () => handleRecordingIcon(),
    },
    PARTICIPANTS: {
      name: 'PARTICIPANTS_LIST',
      isIcon: props.showWaitingArea,
      icon: {
        on: participantsIconOn,
        off: participantsIconOff,
      },
      title: {
        on: 'CLOSE_PARTICIPANTS',
        off: 'OPEN_PARTICIPANTS',
      },
      conditions: true,
      onClick: () => props.handleParticipantsIcon(),
    },
    SAVE: {
      name: 'SAVE',
      title: {
        off: getMessage( 'SAVE' ),
      },
      isIcon: false,
      icon: {
        off: saveIcon,
      },
      conditions: displaySave,
      onClick: () => handleSaveIcon(),
    },
  };

  const handleMaskIcon = () => {
    const message = {
      data: {
        category: socketMessage.category.WEBRTC,
        action: webRtcMessageId.SET_IS_SCREEN_MASKING,
        sessionKey: props.sessionKey,
        screenMaskingEnable: ! states.isScreenMasking,
      },
    };

    // send SHARE_MEDIA req to create a media pipeline
    props.sendWebsocketMessage( socketMessage.events.MESSAGE, message );

    headerAction.setIsScreenMasking( ! states.isScreenMasking );
  };

  const handleRecordingIcon = () => {
    const message = {
      data: {
        category: socketMessage.category.WEBRTC,
        action: webRtcMessageId.SET_IS_RECORDING,
        sessionKey: props.sessionKey,
        recordingEnable: ! states.isRecording,
      },
    };
    // send SHARE_MEDIA req to create a media pipeline
    props.sendWebsocketMessage( socketMessage.events.MESSAGE, message );
    headerAction.setIsRecording( ! states.isRecording );
  };

  const handleSaveIcon = () => {
    setSpinnerVisibility( true );
    sessionActions.setSaveSessionDataFlag( true );
    setTimeout( () => {
      sessionActions.saveSessionData( props.sessionKey ).then(
        () => {
          setSpinnerVisibility( false );
          toastr.success( getMessage( 'SESSION_SAVE_SUCCESS' ) );
        },
        ( error ) => {
          setSpinnerVisibility( false );
          toastr.error( getMessage( 'SESSION_SAVE_ERROR' ) );
        }
      );
    }, 10000 );
  };

  const dashboardMenuIcon = ( icon ) => {
    return (
      <React.Fragment key={ icon.name }>
        { icon.conditions && (
          <Col onClick={ icon.onClick } className="noPadding">
            <div className="dashboardMenuIcons">
              <img
                src={
                  icon.isIcon ? icon.icon.on : icon.icon.off
                }
                alt={ getMessage(
                  icon.isIcon ? icon.title.on : icon.title.off
                ) }
                title={ getMessage(
                  icon.isIcon ? icon.title.on : icon.title.off
                ) }
              />
              <span>{ getMessage( icon.name ) }</span>
            </div>
          </Col>
        ) }
      </React.Fragment>
    );
  };

  return (
    <Row className="dashboardMenu">
      <Spinner showSpinner={ spinnerVisibility } />
      { /** Dashboard Menu Icons */ }
      { Object.keys( dashBoardMenuIcons ).map( ( key ) => {
        return dashboardMenuIcon( dashBoardMenuIcons[ key ] );
      } ) }
    </Row>
  );
};

export default DashboardMenu;
